import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <i className="ri-tools-fill text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">MTA Planner Pro</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Solutions Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('solutions')}
                className="flex items-center space-x-1 text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="font-medium">Solutions</span>
                <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === 'solutions' ? 'rotate-180' : ''}`}></i>
              </button>
              {openDropdown === 'solutions' && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  <Link
                    to="/sole-trader"
                    className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                    onClick={() => setOpenDropdown(null)}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-user-line text-lg"></i>
                      <div>
                        <div className="font-medium">Sole Trader</div>
                        <div className="text-xs text-gray-500">Run your business solo</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/job-marketplace"
                    className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                    onClick={() => setOpenDropdown(null)}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-briefcase-line text-lg"></i>
                      <div>
                        <div className="font-medium">Job Marketplace</div>
                        <div className="text-xs text-gray-500">AI-generated jobs</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/invoice"
                    className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                    onClick={() => setOpenDropdown(null)}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-file-text-line text-lg"></i>
                      <div>
                        <div className="font-medium">AI Invoicing</div>
                        <div className="text-xs text-gray-500">Smart invoices</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/gps-tracking"
                    className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                    onClick={() => setOpenDropdown(null)}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-map-pin-line text-lg"></i>
                      <div>
                        <div className="font-medium">GPS Tracking</div>
                        <div className="text-xs text-gray-500">Live location</div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('industries')}
                className="flex items-center space-x-1 text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="font-medium">Industries</span>
                <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === 'industries' ? 'rotate-180' : ''}`}></i>
              </button>
              {openDropdown === 'industries' && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  <Link to="/industries/plumbing" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Plumbing
                  </Link>
                  <Link to="/industries/electrical" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Electrical
                  </Link>
                  <Link to="/industries/carpentry" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Carpentry
                  </Link>
                  <Link to="/industries/hvac" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    HVAC
                  </Link>
                  <Link to="/industries/painting" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Painting
                  </Link>
                  <Link to="/industries/roofing" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Roofing
                  </Link>
                  <Link to="/industries/landscaping" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Landscaping
                  </Link>
                  <Link to="/industries/tiling" className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => setOpenDropdown(null)}>
                    Tiling
                  </Link>
                </div>
              )}
            </div>

            <Link to="/pricing" className="text-gray-700 hover:text-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer">
              Pricing
            </Link>
            <Link to="/resources" className="text-gray-700 hover:text-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer">
              Resources
            </Link>
            <Link to="/demo" className="text-gray-700 hover:text-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer">
              Demo
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer">
              Contact
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/login"
              className="px-5 py-2.5 text-gray-700 hover:text-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              Log In
            </Link>
            <Link
              to="/signup"
              className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:from-orange-600 hover:to-amber-700 transition-all font-medium shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-orange-600 transition-colors cursor-pointer"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Solutions Section */}
            <div>
              <button
                onClick={() => toggleDropdown('mobile-solutions')}
                className="flex items-center justify-between w-full text-gray-700 font-medium py-2 cursor-pointer"
              >
                <span>Solutions</span>
                <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === 'mobile-solutions' ? 'rotate-180' : ''}`}></i>
              </button>
              {openDropdown === 'mobile-solutions' && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link
                    to="/sole-trader"
                    className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setOpenDropdown(null);
                    }}
                  >
                    Sole Trader
                  </Link>
                  <Link
                    to="/job-marketplace"
                    className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setOpenDropdown(null);
                    }}
                  >
                    Job Marketplace
                  </Link>
                  <Link
                    to="/invoice"
                    className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setOpenDropdown(null);
                    }}
                  >
                    AI Invoicing
                  </Link>
                  <Link
                    to="/gps-tracking"
                    className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setOpenDropdown(null);
                    }}
                  >
                    GPS Tracking
                  </Link>
                </div>
              )}
            </div>

            {/* Industries Section */}
            <div>
              <button
                onClick={() => toggleDropdown('mobile-industries')}
                className="flex items-center justify-between w-full text-gray-700 font-medium py-2 cursor-pointer"
              >
                <span>Industries</span>
                <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === 'mobile-industries' ? 'rotate-180' : ''}`}></i>
              </button>
              {openDropdown === 'mobile-industries' && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link to="/industries/plumbing" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Plumbing
                  </Link>
                  <Link to="/industries/electrical" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Electrical
                  </Link>
                  <Link to="/industries/carpentry" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Carpentry
                  </Link>
                  <Link to="/industries/hvac" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    HVAC
                  </Link>
                  <Link to="/industries/painting" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Painting
                  </Link>
                  <Link to="/industries/roofing" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Roofing
                  </Link>
                  <Link to="/industries/landscaping" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Landscaping
                  </Link>
                  <Link to="/industries/tiling" className="block py-2 text-gray-600 hover:text-orange-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    Tiling
                  </Link>
                </div>
              )}
            </div>

            <Link to="/pricing" className="block py-2 text-gray-700 hover:text-orange-600 font-medium cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              Pricing
            </Link>
            <Link to="/resources" className="block py-2 text-gray-700 hover:text-orange-600 font-medium cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              Resources
            </Link>
            <Link to="/demo" className="block py-2 text-gray-700 hover:text-orange-600 font-medium cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              Demo
            </Link>
            <Link to="/contact" className="block py-2 text-gray-700 hover:text-orange-600 font-medium cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              Contact
            </Link>

            <div className="pt-4 space-y-3 border-t border-gray-100">
              <Link
                to="/login"
                className="block w-full text-center px-5 py-2.5 text-gray-700 hover:text-orange-600 transition-colors font-medium border border-gray-300 rounded-lg whitespace-nowrap cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="block w-full text-center px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:from-orange-600 hover:to-amber-700 transition-all font-medium shadow-lg whitespace-nowrap cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
